import { flow } from 'lodash'

import {
  AddressDetailsDTO,
  BookingDTOWaypointsInner as BookingDTOWaypoints,
  DestinationDoorWaypointDTO,
  DestinationPortWaypointDTO,
  OriginDoorWaypointDTO,
  OriginPortWaypointDTO,
  WaypointBaseDTOTransportMode,
  WaypointConnectionBaseDTO,
  WaypointTypes,
} from '@/clients/booking'
import { waypointIDs } from '@/clients/booking'

export const defaultWaypoints: BookingDTOWaypoints[] = [
  {
    id: waypointIDs.originPort,
    type: WaypointTypes.OriginPort,
    outgoingConnections: [
      {
        nextWaypointID: waypointIDs.destinationPort,
        transportMode: undefined,
      },
    ],
    port: undefined,
  },

  {
    id: waypointIDs.destinationPort,
    type: WaypointTypes.DestinationPort,
    outgoingConnections: undefined,
    port: undefined,
  },
] as BookingDTOWaypoints[]

const updateWaypointsWithType = (
  waypoints: BookingDTOWaypoints[],
  waypointType: WaypointTypes | undefined,
  updater: (w: BookingDTOWaypoints) => BookingDTOWaypoints
) => waypoints.map((w) => (w.type === waypointType ? updater(w) : w))

const updateOutgoingConnectionsForType = (
  waypoints: BookingDTOWaypoints[],
  waypointType: WaypointTypes,
  updater: (outgoingConnection?: WaypointConnectionBaseDTO) => WaypointConnectionBaseDTO
) =>
  waypoints.map((w) =>
    w.type === waypointType
      ? {
          ...w,
          outgoingConnections: [updater(w.outgoingConnections?.[0] || ({} as WaypointConnectionBaseDTO))],
        }
      : w
  )

function removeWaypointTypes(
  waypoints: BookingDTOWaypoints[] = [],
  waypointType: WaypointTypes
): BookingDTOWaypoints[] {
  const relatedOutgoingConnectionType: Partial<Record<WaypointTypes, WaypointTypes>> = {
    [WaypointTypes.DestinationDoor]: WaypointTypes.DestinationPort,
  }

  return flow(
    (waypoints: BookingDTOWaypoints[]) => waypoints.filter((w: BookingDTOWaypoints) => w.type !== waypointType),
    // Remove the outgoing connection of the related waypoint type
    // If we remove destination door, we should also remove the outgoing connection of the destination port
    // which is connected to the destination door
    (waypoints: BookingDTOWaypoints[]) =>
      updateWaypointsWithType(waypoints, relatedOutgoingConnectionType[waypointType], (w) => ({
        ...w,
        outgoingConnections: undefined,
      }))
  )(waypoints)
}

export const getWaypointType = (waypoints: BookingDTOWaypoints[] = [], waypointType: WaypointTypes) =>
  waypoints.find((w) => w.type === waypointType)

export const hasWaypointTypes = (waypoints: BookingDTOWaypoints[] = [], waypointType: WaypointTypes) =>
  waypoints.some((w) => w.type === waypointType)

// We assume that origin and destination port waypoints are always present in the waypoints array
export function setMainCarriageTransportMode(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints,
  transportMode?: WaypointBaseDTOTransportMode
): BookingDTOWaypoints[] {
  return updateOutgoingConnectionsForType(waypoints, WaypointTypes.OriginPort, (outgoingConnection) => ({
    ...outgoingConnection,
    nextWaypointID: outgoingConnection?.nextWaypointID || waypointIDs.destinationPort,
    transportMode,
  }))
}

export function getMainCarriageTransportMode(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints
): WaypointBaseDTOTransportMode | undefined {
  return getWaypointType(waypoints, WaypointTypes.OriginPort)?.outgoingConnections?.[0]?.transportMode
}

export function getPreCarriageTransportMode(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints
): WaypointBaseDTOTransportMode | undefined {
  return getWaypointType(waypoints, WaypointTypes.OriginDoor)?.outgoingConnections?.[0]?.transportMode
}

export function getOnCarriageTransportMode(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints
): WaypointBaseDTOTransportMode | undefined {
  return getWaypointType(waypoints, WaypointTypes.DestinationPort)?.outgoingConnections?.[0]?.transportMode
}

export function setPreCarriageTransportMode(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints,
  transportMode?: WaypointBaseDTOTransportMode
): BookingDTOWaypoints[] {
  return updateOutgoingConnectionsForType(waypoints, WaypointTypes.OriginDoor, (outgoingConnection) => ({
    ...outgoingConnection,
    nextWaypointID: outgoingConnection?.nextWaypointID || waypointIDs.originPort,
    transportMode,
  }))
}

export function setOnCarriageTransportMode(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints,
  transportMode?: WaypointBaseDTOTransportMode
): BookingDTOWaypoints[] {
  return updateOutgoingConnectionsForType(waypoints, WaypointTypes.DestinationPort, (outgoingConnection) => ({
    ...outgoingConnection,
    nextWaypointID: outgoingConnection?.nextWaypointID || waypointIDs.destinationDoor,
    transportMode,
  }))
}

export function getOriginPort(waypoints: BookingDTOWaypoints[] = defaultWaypoints): string | undefined {
  return (getWaypointType(waypoints, WaypointTypes.OriginPort) as OriginPortWaypointDTO)?.port
}

export function getDestinationPort(waypoints: BookingDTOWaypoints[] = defaultWaypoints): string | undefined {
  return (getWaypointType(waypoints, WaypointTypes.DestinationPort) as DestinationPortWaypointDTO)?.port
}

// We assume that origin and destination port waypoints are always present in the waypoints array
export function setOriginPort(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints,
  originPort?: string
): BookingDTOWaypoints[] {
  return updateWaypointsWithType(waypoints, WaypointTypes.OriginPort, (w) => ({ ...w, port: originPort }))
}

// We assume that origin and destination port waypoints are always present in the waypoints array
export function setDestinationPort(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints,
  destinationPort?: string
): BookingDTOWaypoints[] {
  return updateWaypointsWithType(waypoints, WaypointTypes.DestinationPort, (w) => ({ ...w, port: destinationPort }))
}

export function getPickupAddress(waypoints: BookingDTOWaypoints[] = []): AddressDetailsDTO | undefined {
  return (getWaypointType(waypoints, WaypointTypes.OriginDoor) as OriginDoorWaypointDTO)?.address
}

export function getDeliveryAddress(waypoints: BookingDTOWaypoints[] = []): AddressDetailsDTO | undefined {
  return (getWaypointType(waypoints, WaypointTypes.DestinationDoor) as DestinationDoorWaypointDTO)?.address
}

export function setPickupAddress(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints,
  pickupAddress?: AddressDetailsDTO
): BookingDTOWaypoints[] {
  if (pickupAddress == null) {
    return removeWaypointTypes(waypoints, WaypointTypes.OriginDoor)
  }

  if (hasWaypointTypes(waypoints, WaypointTypes.OriginDoor)) {
    return flow(
      (waypoints: BookingDTOWaypoints[]) =>
        updateWaypointsWithType(waypoints, WaypointTypes.OriginDoor, (w) => ({ ...w, address: pickupAddress })),
      (waypoints: BookingDTOWaypoints[]) =>
        updateOutgoingConnectionsForType(waypoints, WaypointTypes.OriginDoor, (outgoingConnection) => ({
          ...outgoingConnection,
          nextWaypointID: waypointIDs.originPort,
          transportMode: outgoingConnection?.transportMode || WaypointBaseDTOTransportMode.Truck,
        }))
    )(waypoints)
  } else {
    return [
      {
        id: waypointIDs.originDoor,
        type: WaypointTypes.OriginDoor,
        address: pickupAddress,
        outgoingConnections: [
          {
            nextWaypointID: waypointIDs.originPort,
            transportMode: WaypointBaseDTOTransportMode.Truck,
          },
        ],
      },
      ...waypoints,
    ]
  }
}

export function setDeliveryAddress(
  waypoints: BookingDTOWaypoints[] = defaultWaypoints,
  deliveryAddress?: AddressDetailsDTO
): BookingDTOWaypoints[] {
  if (deliveryAddress == null) {
    return removeWaypointTypes(waypoints, WaypointTypes.DestinationDoor)
  }

  if (hasWaypointTypes(waypoints, WaypointTypes.DestinationDoor)) {
    return flow(
      (waypoints: BookingDTOWaypoints[]) =>
        updateWaypointsWithType(waypoints, WaypointTypes.DestinationDoor, (w) => ({ ...w, address: deliveryAddress })),
      (waypoints: BookingDTOWaypoints[]) =>
        updateOutgoingConnectionsForType(waypoints, WaypointTypes.DestinationPort, (outgoingConnection) => ({
          ...outgoingConnection,
          nextWaypointID: waypointIDs.destinationDoor,
          transportMode: outgoingConnection?.transportMode || WaypointBaseDTOTransportMode.Truck,
        }))
    )(waypoints)
  } else {
    return [
      ...updateOutgoingConnectionsForType(waypoints, WaypointTypes.DestinationPort, (outgoingConnection) => ({
        ...outgoingConnection,
        nextWaypointID: waypointIDs.destinationDoor,
        transportMode: outgoingConnection?.transportMode || WaypointBaseDTOTransportMode.Truck,
      })),
      {
        id: waypointIDs.destinationDoor,
        type: WaypointTypes.DestinationDoor,
        address: deliveryAddress,
      },
    ]
  }
}
